/**
 * Counting the drawer.
 *
 * A closing records `actualCash` — one number. Where that number came from is
 * the part that makes a difference explainable: "we are ৳500 short" is a
 * problem, "we are one ৳500 note short" is a lead. Shops already count the
 * notes; this is the shape of that count.
 */
import { add, money, round, toAmountString, type Money } from './money';

/**
 * Bangladeshi notes and coins, largest first.
 *
 * Largest first because that is the order a drawer is counted in, and the order
 * the printed Daily Sales Report lists them. The database constrains
 * `denomination` to exactly this set, so adding one here means a migration.
 */
export const CASH_DENOMINATIONS = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1] as const;

export type CashDenomination = (typeof CASH_DENOMINATIONS)[number];

/** One line of a count: how many of a given note or coin. */
export type DenominationEntry = { denomination: number; quantity: number };

/** A counted line, with the money it comes to. */
export type DenominationLine = DenominationEntry & { amount: Money };

export function isCashDenomination(value: number): value is CashDenomination {
  return (CASH_DENOMINATIONS as readonly number[]).includes(value);
}

/** What one line of a count is worth. */
export function denominationAmount(entry: DenominationEntry): Money {
  return round(money(entry.denomination).times(entry.quantity));
}

/**
 * What a whole count comes to.
 *
 * Lines with a zero quantity contribute nothing but are kept by the caller:
 * "we looked and there were none" is a different statement from "we did not
 * count this", and only the first should survive into the record.
 */
export function denominationTotal(entries: readonly DenominationEntry[]): Money {
  return entries.reduce<Money>((total, entry) => add(total, denominationAmount(entry)), money(0));
}

/** Expand a count into storable lines, in the order the drawer is counted. */
export function denominationLines(entries: readonly DenominationEntry[]): DenominationLine[] {
  const byDenomination = new Map<number, number>();
  for (const entry of entries) {
    byDenomination.set(entry.denomination, (byDenomination.get(entry.denomination) ?? 0) + entry.quantity);
  }
  return CASH_DENOMINATIONS.filter((denomination) => byDenomination.has(denomination)).map(
    (denomination) => {
      const quantity = byDenomination.get(denomination) ?? 0;
      return { denomination, quantity, amount: denominationAmount({ denomination, quantity }) };
    },
  );
}

export type DenominationCheck =
  | { ok: true; total: Money }
  | { ok: false; total: Money; difference: Money; message: string };

/**
 * Does the count add up to the cash it claims to explain?
 *
 * Deliberately exact rather than tolerant. A tolerance on a cash *difference*
 * is a judgement about how well a busy shop can reconcile; a tolerance here
 * would only ever be hiding arithmetic, because the notes either add up to the
 * figure typed in the box or one of the two was entered wrong.
 */
export function checkDenominationTotal(
  entries: readonly DenominationEntry[],
  actualCash: Money | string | number,
): DenominationCheck {
  const total = denominationTotal(entries);
  const actual = round(money(actualCash));
  if (total.equals(actual)) return { ok: true, total };

  const difference = round(total.minus(actual));
  const over = difference.greaterThan(0);
  return {
    ok: false,
    total,
    difference,
    message: `The notes counted come to ${toAmountString(total)}, but the cash counted is recorded as ${toAmountString(actual)} — ${toAmountString(difference.abs())} ${over ? 'more' : 'less'}. Check the count before closing.`,
  };
}
